<template>
  <div
    class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 border border-blueGray-200 rounded-lg"
  >
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center">
        <div class="relative w-full px-2 max-w-full flex-grow flex-1">
          <h3 class="text-base text-blueGray-700">
            <i class="pi pi-barcode"></i> Plan Subscription List
          </h3>
        </div>
        <div
          class="relative w-full px-4 max-w-full flex-grow flex-1 flex flex-wrap items-center justify-end"
          style="gap: 0.25rem;"
        >
          <button
            v-for="preset in datePresets"
            :key="preset.label"
            type="button"
            class="text-xs px-3 py-2 rounded outline-none focus:outline-none mr-1"
            :class="activePreset === preset.label
              ? 'bg-blueGray-900 text-white'
              : 'bg-blueGray-200 text-blueGray-600 hover:bg-blueGray-300'"
            @click="applyDatePreset(preset)"
          >
            {{ preset.label }}
          </button>
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs uppercase px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 max-w-full"
            type="button"
            @click="$emit('filter')"
          >
            <i class="pi pi-filter" style="font-size: 10px;"></i> filter
          </button>
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs uppercase px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 max-w-full"
            type="button"
            @click="$emit('download')"
            :disabled="loaders().fetchSummary || loaders().getDashboard"
          >
            <i class="pi pi-download" style="font-size: 10px;"></i> download
          </button>
        </div>
      </div>
    </div>
    <div class="block w-full overflow-x-auto">
      <!-- Projects table -->
      <table class="items-center w-full bg-transparent border-collapse">
        <thead class="thead-light">
          <tr>
            <th
              class="px-6 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left"
            >
              <i class="pi pi-user mr-1"></i>User
            </th>
            <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left"
            >
              <i class="pi pi-star mr-1"></i>Reviews
            </th>
            <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left"
            >
              <i class="pi pi-list-check mr-1"></i>Plan
            </th>
            <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left min-w-140-px"
            >
              <i class="pi pi-dollar mr-1"></i>Amount
            </th>
            <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left min-w-140-px"
            >
              <i class="pi pi-flag mr-1"></i>Status
            </th>
            <!-- <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left min-w-140-px"
            >
              <i class="pi pi-chart-line mr-1"></i>Profit/Loss
            </th>
            <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left min-w-140-px"
            >
              <i class="pi pi-percentage mr-1"></i>Rate
            </th> -->
            <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left min-w-140-px"
            >
              <i class="pi pi-file mr-1"></i>Summary
            </th>
            <th
              class="px-2 bg-blueGray-50 text-blueGray-500 align-middle border border-solid border-blueGray-100 py-3 text-xs border-l-0 border-r-0 whitespace-nowrap text-left min-w-140-px"
            >
              <i class="pi pi-calendar mr-1"></i>Date
            </th>
          </tr>
        </thead>
        <tbody v-if="has('user.plan.view')">
          <template v-if="!loaders().fetchPlanUser">
            <tr v-for="(userPlan, i) in planList" :key="i" class="cursor-pointer" @mouseenter="userPlanIdx = i">
              <th
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left"
                :class="{
                  'text-red-500': !userPlan.user?.settings?.is_verified
                }"
              >
                <div class="flex" @click="showUserModal(userPlan)">
                  {{ userPlan.user?.name }} 
                </div>
                <small class="text-blueGray-400 cursor-pointer" @click.prevent="clickToCopy(userPlan.user?.email)">
                  {{ userPlan.user?.email }}  <i v-if="userPlan.user?.email" class="pi pi-clone text-xs" ></i>
                </small>
              </th>
              <th
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left"
                :class="{
                  'text-red-500': !userPlan.user?.settings?.is_verified
                }"
              >
                <review-dropdown :reviews="userPlan.user?.reviews" model="user" :model_id="userPlan.user?.id"></review-dropdown>
              </th>
              <td
                class="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
                @click="showUserPlanModal(userPlan)"
              >
                {{ userPlan.plan?.name }}
              </td>
              <td
                class="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
                @click="showUserPlanModal(userPlan)"
              >
                <div class="flex items-center">
                  <small class="text-xs">{{ formatter(userPlan.currency).format(userPlan.amount) }}</small>
                  <!-- <span class="px-2 py-1 bg-warmGray-400 rounded-lg" v-for="(tag, j) in JSON.parse(plan.risk_type)" :key="j">{{ tag }}</span> -->
                </div>
              </td>
              <td
                class="border-t-0 px-2 align-middle Capitalize border-l-0 border-r-0 text-xs whitespace-nowrap p-4 cursor-pointer"
                @click="selectPlan(userPlan)"
                :class="{
                  'text-blueGray-900': userPlan?.status.toUpperCase() === 'ACTIVE',
                  'text-red-500': userPlan?.status.toUpperCase() === 'TERMINATED',
                  'text-orange-500': userPlan?.status.toUpperCase() === 'INITIATED',
                  'text-blueGray-400': userPlan?.status.toUpperCase() === 'PENDING'
                }"
              >
                {{ userPlan?.status }}
              </td>
              <td
                class="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 overflow-hidden"
              >
                <div v-if="['PENDING', 'INITIATED', 'APPROVED'].includes(userPlan?.status)">
                  <button
                    class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    @click.self="processPlan(userPlan, 'disburse', 'disbursement')"
                    v-if="has('loan.confirm') && userPlan?.status === 'APPROVED'"
                  >
                    <i class="pi pi-check-square"></i> disburse
                  </button>
                  <button
                    class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    @click.self="processPlan(userPlan, 'approved', 'approval')"
                    v-else-if="userPlan?.status === 'INITIATED'"
                  >
                  <i class="pi pi-check-circle"></i> approve
                  </button>
                  <button
                    class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    @click.self="processPlan(userPlan, 'approved', 'approval')"
                    v-else-if="userPlan?.status === 'PENDING' && is('business_manager')"
                  >
                  <i class="pi pi-check-circle"></i> approve
                  </button>
                  <button
                    class="bg-red-500 text-white active:bg-red-600 text-xs px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    @click.stop="processPlan(userPlan, 'decline')"
                    v-if="has('plan.terminate.ability') || has('plan.terminate')"
                  >
                  <i class="pi pi-times-circle"></i> decline
                  </button>
                </div>
                <div 
                v-else-if="['ACTIVE', 'COMPLETED', 'RETRY', 'DUE', 'DEFAULT'].includes(userPlan?.status)" class="flex flex-inline"
                @click="selectPlan(userPlan)"
                >
                  <template v-if="userPlan.loans && userPlan.loans.length > 0">
                    <installment-status-icon
                      v-for="(loan, i) in userPlan.loans.slice(0, 7)" :key="i"
                      :status="loan.status"
                    />
                    <span v-if="userPlan.loans.length > 7" class="text-blueGray-400 text-xs ml-1 self-center">...</span>
                  </template>
                  <template v-else-if="userPlan.savings && userPlan.savings.length > 0">
                    <installment-status-icon
                      v-for="(save, i) in userPlan.savings.slice(0, 7)" :key="i"
                      :status="save.status"
                      :paidExtra="['WITHDRAWN']"
                    />
                    <span v-if="userPlan.savings.length > 7" class="text-blueGray-400 text-xs ml-1 self-center">...</span>
                  </template>
                  <template v-else-if="userPlan.investments && userPlan.investments.length > 0">
                    <installment-status-icon
                      v-for="(investment, i) in userPlan.investments.slice(0, 7)" :key="i"
                      :status="investment.status"
                      :paidExtra="['WITHDRAWN']"
                    />
                    <span v-if="userPlan.investments.length > 7" class="text-blueGray-400 text-xs ml-1 self-center">...</span>
                  </template>
                  <template v-else>
                    <i>no payment plan</i>
                  </template>
                </div>
                <button
                  class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  @click="selectPlan(userPlan)"
                >
                  <i class="pi pi-eye"></i> see more
                </button>
              </td>
              <td
                class="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
              >
                {{ moment(userPlan.created_at).format("DD MMM YY") }}
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="8" rowspan="8">
                <div class="flex items-center justify-center w-full py-12 h-30">
                  <span class="loader block"></span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <permission-err v-if="!has('user.plan.view')"></permission-err>
      <empty-list v-if="(!planList || planList.length === 0) && !loaders().fetchPlanUser && has('user.plan.view')"></empty-list>
    </div>
    <div class="flex flex-wrap items-center">
      <div class="relative w-full px-4 max-w-full flex-grow flex-1">
        <!-- {{ plans }} -->
        <circular-pagination :current_page="plans.current_page" :last_page="plans.last_page" :size="pageSize" @paginate="pageChange" ></circular-pagination>
      </div>
    </div>

    <transition name="bounce" :duration="{ enter: 500, leave: 800 }">
      <div v-if="modals.user" style="width: 100%;" class="overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none justify-center items-center flex w-screen h-screen top-0 left-0">
        <div class="relative w-auto my-6 mx-auto">
          <!--content-->
          <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            <!--header-->
            <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 class="text-lg capitalize">
                <i class="pi pi-info-circle"></i> {{  user?.name }} information
              </h3>
              <button 
                class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none" 
                v-on:click="toggleModal('user', false)"
              >
                <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                  ×
                </span>
              </button>
            </div>
            <!--body-->
            <recursive-table 
              :data="user" 
              :excludeAll="['id', 'is_password_auto_gen', 'usd_wallet', 'user_id', 'fcm_token']"
              :imagesAll="['logo', 'banner', 'image']"
              :telsAll="['phone']"
              :tels="['profile.phone']"
              :emailsAll="['email', 'support_email']"
              :datesAll="['created_at', 'updated_at', 'email_verified_at']"
              :copies="['phone', 'profile.phone']" 
            />
            <div class="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
              <button
                class="bg-red-500 text-white active:bg-red-600 text-xs px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                type="button"
                @click="handleRemoveFromCluster(userPlan, cluster)"
              >
                Revome {{ plan.user?.name }} from cluster
              </button>

              <button 
                class="text-red-500 background-transparent px-3 py-1 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button" 
                v-on:click="toggleModal('user', false)"
              >
                close
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="modals.user" style="width: 100%; opacity: 0.25;" class="opacity-25 fixed inset-0 z-40 h-screen top-0 left-0 bg-black"></div>
    
    <user-plan-modal :userPlan="plan" :show="modals.userPlan" @close="() => modals.userPlan = false"></user-plan-modal>
  </div>
</template>

<script>
import CircularPagination from "@/components/Paginations/CircularPagination.vue";
import UserPlanModal from "@/components/Modals/UserPlanModal.vue";
import { mapActions, mapGetters } from 'vuex';
import { formatter } from "../../utils/formatter.util";
import moment from "moment"
import ReviewDropdown from "@/components/Dropdowns/UserReviewDropdown.vue";
import { clickToCopy, confirmAction } from "@/utils";
import RecursiveTable from '@/components/Tables/RecursiveTable.vue';
import EmptyList from "@/components/Cards/EmptyList.vue";
import PermissionErr from "@/components/Cards/PermissionErr.vue";
import InstallmentStatusIcon from "@/components/Cards/InstallmentStatusIcon.vue";

export default {
  components: {
    CircularPagination,
    UserPlanModal,
    ReviewDropdown,
    RecursiveTable,
    EmptyList,
    PermissionErr,
    InstallmentStatusIcon
  },
  props: ["plans", "param"],
  created() {
    this.moment = moment
    this.formatter = formatter
    this.clickToCopy = clickToCopy
  },
  data() {
    return {
      planList: [],
      plan: {},
      pagination: {},
      modals: {},
      userPlanIdx: null,
      cluster: {},
      activePreset: null,
      datePresets: [
        { label: 'Today', days: 1 },
        { label: '7D', days: 7 },
        { label: '30D', days: 30 }
      ]
    }
  },
  watch: {
    plans: function (n) {
      this.planList = n?.data || []
    }
  },
  mounted() {
      this.planList = this.plans?.data || []
  },
  methods: {
    ...mapActions(["fetchPlanUser", "confirmLoan", "updatePlanUser", "leaveBusiness"]),
    ...mapGetters(["loaders", "dashboard"]),
    selectPlan(plan) {
      if (this.has("plans.show")) {
        this.$emit('selectedPlan', plan)
      }
    },
    pageChange(pagination) {
      this.pagination = pagination;
      this.fetchNewPageUser();
    },
    // Today/7D/30D shortcuts — forwards to Plans.vue, which calls the same
    // filter()/applyDatePreset() path on PlanStats.vue's $ref that its own
    // FilterModal already goes through, keeping this consistent with manual
    // date filtering instead of being a parallel, easily-diverging mechanism.
    applyDatePreset(preset) {
      this.activePreset = preset.label;
      this.$emit('applyPreset', preset);
    },
    getPlanBalance(plan) {
        return plan.user_purchased_coins.reduce( (t, coin) => t + ( coin.price_sold ? Math.round((parseFloat(coin.price_sold) - parseFloat(coin.price_bought)) * 10)/10 : 0), 0 )
    },
    getPlanSpent(plan) {
        return plan.user_purchased_coins.reduce( (t, coin) => t + ( coin.price_sold ? Math.round(parseFloat(coin.price_bought) * 10)/10 : 0), 0 )
    },
    fetchNewPageUser() {
      const params = {
        ...this.pagination,
        ...this.param
      }
      this.fetchPlanUser(params)
    },
    processPlan(plan, status, adj) {
      confirmAction({
        confirmText: `${plan?.user?.name } ${plan.name } ${ plan.type}`,
        confirmTextHelper: `Kindly confirm you want to ${status} this loan`,
        successText: `You have successfully ${status} ${plan?.user?.name} ${plan.name} ${ plan.type}`,
        cancelText: `${adj || status} of plan aborted`
      }, () => {
        const initialStatus = plan.status;
        const newStatus = status.toUpperCase()
        this.planList = this.planList.map(p => p.id === plan.id ? {...p, status: newStatus} : p);
        const data = {...plan, status: newStatus}
        this.updatePlanUser(data).then(() => {})
        .catch( () => this.planList = this.planList.map(p => p.id === plan.id ? {...p, status: initialStatus} : p))
      });
    },
    showUserPlanModal: function(plan = {}){
      this.plan = plan;
      this.modals.userPlan = true;
    },
    showUserModal: function(plan = {}){
      this.plan = plan;
      this.cluster = this.dashboard().business
      this.user = plan.user; 
      this.toggleModal('user', true)
    },
    toggleModal(modalName, isvisible) {
      this.modals[modalName] = isvisible
    },
    async handleRemoveFromCluster(userPlan, cluster) {
      if (!confirm("Are you sure you want to remove this user from this cluster?")){
        return
      }
      await this.leaveBusiness({userPlan: userPlan, code: cluster.code})
    }
  }
}
</script>

<style scoped>
.bounce-enter-active {
  animation: bounce-in .5s;
}
.bounce-leave-active {
  animation: bounce-in .5s reverse;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>